import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CellContext } from '@tanstack/react-table'
import to from 'await-to-js'
import { DataTable, useDataTableContext } from 'components/DataTable'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import * as S from 'components/Tables/CommonTable.styles'
import dialogConfirm from 'components/dialogConfirm'
import { DealReport, isDealReport } from 'models/deals_reports'
import { PaginatedResponse } from 'models/paginated-response'

// Union type for both Deal and DealReport
import { useCallback, useEffect, useMemo, useState } from 'react'
import { OverlayTrigger } from 'react-bootstrap'
import {
  BsDownload,
  BsFileEarmarkPdf,
  BsPencil,
  BsPerson,
  BsSignpost,
  BsTags,
  BsTruck,
  BsXCircle,
} from 'react-icons/bs'
import { toast } from 'react-toastify'
import apiService from 'services/api'
import { IContactCompany } from '../../../models/contact_companies'
import { FilterIdentifier } from '../../../models/saved_view'

import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import {
  ActionsDropdown,
  createDeleteAction,
  createDownloadAction,
  createEditAction,
} from '../../Buttons/ActionsDropdown'
import TableButtonAction from '../../Buttons/TableButtons/TableButtonAction'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../DataTable/UI'

import { TableSearch } from '../../Filters/TableSearch'
import { FilterChips } from '../../Filters/components/FilterChips/FilterChips'
import DistributorAssignModal from '../../Modals/AssignmentModal/DistributorAssignModal'
import GPOAssignModal from '../../Modals/AssignmentModal/GPOAssignModal'
import TagAssignModal from '../../Modals/AssignmentModal/TagAssignModal'
import DealExportModal from '../../Modals/ExportModal/DealExportModal'
import DealsOverrideModal from '../../Modals/FormActionModal/DealsOverrideModal'
import AccountOwnerSelectModal from '../../Modals/SelectModal/AccountOwnerSelectModal'
import SalesStageSelectModal from '../../Modals/SelectModal/SalesStageSelectModal'
import { cn } from '../../UI/cn'
import { useBulkDealsDeleter } from './useBulkDealsDeleter'
import { PiShippingContainer, PiCalendarStar } from 'react-icons/pi'
import { EditDealModalV2 } from 'components/Deals/EditDealModal/EditDealModalV2'
import { useDealsTableColumnsV2 } from './useDealsTableColumnsV2'
import { ExportButton, PAGE_SIZE, tooltip } from './DealsTable'
import { DealsFiltersetV2 } from './DealsFiltersetV2'
import ReminderModal from 'components/Reminders/ReminderModal'
import { featureFlagService } from 'utils/featureFlagService'
import { DealsTableBaseFilters } from './DealsTableTypes'
import { useBulkDownload } from '../../../features/bulk-download-specsheets/BulkDownloadContext'

const sortableFields: (keyof DealReport)[] = [
  'id',
  'cc_company_name',
  'deal_name',
  'product_variant_name',
  'product_variant_name',

  'monthly_volume_predicted',
  'monthly_volume_override',
  'monthly_revenue_predicted',
  'monthly_revenue_override',
  'annual_volume',
  'annual_revenue',
  'annual_total',
  'annual_brand_opp',

  'close_date_override',
  'sales_stage_name',
  'account_owner_name',
  'created',
  'modified',
  'last_datetime_stage_changed',
  'expected_days_to_close',
  'cc_note_count',
  'cc_category_name',
  'cc_sub_category_name',
  'cc_contact_count',
  'days_since_last_modified',
  'cc_doors_cnt',
  'estimated_meals_per_day',
  'days_since_sent',
  'days_since_reply',
  'days_since_deal_last_modified',
  'doors_count',
  'cc_distributors',
  'cc_gpos',
  'operational_schools',
]

interface DealsTableV2Props {
  setTotalRowsCount?: (count: number) => void
  forCompany?: Partial<IContactCompany>
  forDoorProxyId?: number
  showCompanyColumn?: boolean
  enablePagination?: boolean
  disableUrlPagination?: boolean
  baseFilters?: DealsTableBaseFilters
  tableKey: string
  filterIdentifierModifier?: string
}

export function DealsTableComponentV2(props: DealsTableV2Props) {
  const api = apiService()
  const featureFlag = featureFlagService()
  const queryClient = useQueryClient()

  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()
  const {
    state: { totalSelectedRows },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()

  const [dealReportToEdit, setDealReportToEdit] = useState<DealReport>()
  const [showAssignTags, setShowAssignTags] = useState(false)
  const [showAssignDistributors, setShowAssignDistributors] = useState(false)
  const [showAssignGPOs, setShowAssignGPOs] = useState(false)
  const [showSelectAccountOwner, setShowSelectAccountOwner] = useState(false)
  const [showSelectSalesStage, setShowSelectSalesStage] = useState(false)
  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const IDENTIFIER = ('REPORT_DEALS_TABLE' +
    (props.filterIdentifierModifier ?? '')) as FilterIdentifier

  const dealsFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const { page, setPage } = dealsFilterStore()
  const filtersAsParams = {
    ...props.baseFilters,
    ...useFilterParams(dealsFilterStore),
  }

  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }

    if (props.forDoorProxyId) {
      params['door_proxy_id'] = props.forDoorProxyId
      params['door_only'] = true
    } else if (props.forCompany) {
      params['company'] = props.forCompany.id
    }
    return params
  }, [filtersAsParams, props.forCompany, props.forDoorProxyId])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
    }),
    [filterParams, sortParams]
  )

  const TABLE_QUERY_KEY = [
    ...getTableQueryKey({
      tableKey: props.tableKey,
      filterParams: filterAndSortParams,
      page: page,
    }),
    'reports',
  ]

  const { isFetching, data } = useQuery<PaginatedResponse<DealReport>>({
    queryKey: TABLE_QUERY_KEY,
    queryFn: ({ signal }) => {
      clearSelectedRows()
      return api.getReportDeals(
        {
          ...filterAndSortParams,
          page: page,
          limit: PAGE_SIZE,
        },
        signal
      ) as Promise<PaginatedResponse<DealReport>>
    },
  })

  useEffect(() => {
    if (data?.count != null) {
      props.setTotalRowsCount?.(data.count)
      setTotalRowsInBackend(data.count)
    }
  }, [data])

  // END DATA FETCHING

  const bulkDeleteDeals = useBulkDealsDeleter({
    data: data?.results ?? [],
    accessorKey: 'id',
    tableQueryKey: TABLE_QUERY_KEY,
    filterParams: filterAndSortParams,
    useReportsEndpoint: true,
  })

  // Get selected deal IDs and company ID
  const {
    state: { rowSelection },
  } = useDataTableContext()

  const selectedDealIds = useMemo(() => {
    if (!rowSelection || !data?.results) return []
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((index) => data.results[parseInt(index)]?.id)
      .filter(Boolean)
  }, [rowSelection, data?.results])

  const selectedCompanyId = useMemo(() => {
    if (selectedDealIds.length !== 1 || !data?.results) return undefined
    const selectedDeal = data.results.find((d) => d.id === selectedDealIds[0])
    return selectedDeal?.cc_id // In V2, company ID is cc_id
  }, [selectedDealIds, data?.results])

  // Bulk download spec sheets
  const { startDownload: startBulkDownload, state: bulkDownloadState } =
    useBulkDownload()

  const handleBulkDownloadSpecsheets = useCallback(() => {
    if (!selectedDealIds.length) {
      toast.warning('Please select at least one deal')
      return
    }

    // Create a map of deal IDs to deal names for the download widget
    const dealNames = new Map<number, string>()
    selectedDealIds.forEach((dealId) => {
      const deal = data?.results?.find((d) => d.id === dealId)
      if (deal) {
        const displayName = deal.deal_name
          ? `${deal.deal_name} (${dealId})`
          : deal.cc_company_name
            ? `${deal.cc_company_name} (${dealId})`
            : `Deal ${dealId}`
        dealNames.set(dealId, displayName)
      }
    })

    startBulkDownload(selectedDealIds, dealNames, 'deal_reports')
  }, [selectedDealIds, data?.results, startBulkDownload])

  const handleDownloadSpecsheet = useCallback(
    (dealReport: DealReport) => {
      if (!dealReport.product_variant_id) return

      // Only allow if not already downloading
      if (
        bulkDownloadState.status === 'downloading' ||
        bulkDownloadState.status === 'preparing'
      ) {
        toast.info('A download is already in progress')
        return
      }

      // Use bulk download widget for individual downloads to track progress
      const dealName =
        dealReport.deal_name ||
        dealReport.cc_company_name ||
        `Deal ${dealReport.id}`
      const variantNames = new Map<number, string>([
        [dealReport.product_variant_id, dealName],
      ])
      startBulkDownload([dealReport.product_variant_id], variantNames)
    },
    [bulkDownloadState.status, startBulkDownload]
  )

  async function handleDealDelete(dealId: number) {
    if (
      await dialogConfirm(
        <>
          Are you sure you want to delete this <b>deal</b>? This action cannot
          be undone.
        </>,
        'Delete Deal?',
        '',
        { confirmLabel: 'Delete' }
      )
    ) {
      const [err] = await to(api.deleteDeal(dealId))
      if (!err) {
        toast.success('Deal deleted successfully')
        // Invalidate all queries for this table to ensure cache is properly cleared
        await queryClient.invalidateQueries({
          queryKey: [props.tableKey],
          exact: false,
        })
      }
    }
  }

  const columns = [
    ...useDealsTableColumnsV2({
      showCompanyColumn: props.showCompanyColumn,
      sorting,
    }),
    {
      id: 'actions',
      header: 'Actions',
      size: 100,
      meta: {
        isEditable: false,
        headerDisableOrdering: true,
        isLastColumn: true,
        rightAlign: true,
      },
      cell: (info: CellContext<any, unknown>) => {
        const value = info.row.original
        if (isDealReport(value)) {
          const actions = [
            createEditAction(() => setDealReportToEdit(value)),
            createDeleteAction(() => handleDealDelete(value.id)),
          ]

          // Add download spec sheet action if the deal has a variant with Syndigo data
          if (
            featureFlag.enableSyndigoIntegration &&
            value.product_variant_id
          ) {
            actions.push(
              createDownloadAction('Download Spec Sheet', () =>
                handleDownloadSpecsheet(value)
              )
            )
          }

          return <ActionsDropdown actions={actions} containerWidth={180} />
        }
      },
    },
  ]

  return (
    <DataTableContainer key={props.tableKey + '-container'}>
      <DataTable
        tableHeader={
          <S.SearchContainer>
            <div className="flex w-full gap-2 items-center">
              <TableSearch
                className={'flex-1'}
                filterStore={dealsFilterStore}
                searchPlaceholder="Search by name, company..."
                tableKey={props.tableKey}
              />
              <DealsFiltersetV2
                filterIdentifier={IDENTIFIER}
                hideCampaignsFilter={!!props.baseFilters?.origin_campaign_id}
              />
              <ColumnSelectorRecipient tableKey={props.tableKey} />
            </div>

            <div className={cn('flex flex-row justify-between gap-4')}>
              <TableButtonAction
                items={[
                  {
                    label: 'Manage Tags',
                    icon: <BsTags size={20} />,
                    callback: () => setShowAssignTags(true),
                  },
                  {
                    label: 'Add Known Distributors',
                    icon: <BsTruck size={20} />,
                    callback: () => setShowAssignDistributors(true),
                  },
                  {
                    label: 'Add GPO',
                    icon: <PiShippingContainer size={20} />,
                    callback: () => setShowAssignGPOs(true),
                  },
                  {
                    label: 'Schedule Reminder',
                    icon: <PiCalendarStar size={20} />,
                    callback: () => setShowReminderModal(true),
                    disabled: selectedDealIds.length !== 1,
                    disabledTooltipText:
                      'Please select exactly one deal to schedule a reminder',
                  },
                  ...(featureFlag.enableSyndigoIntegration
                    ? [
                        {
                          label: 'Download Spec Sheets',
                          icon: <BsFileEarmarkPdf size={20} />,
                          callback: handleBulkDownloadSpecsheets,
                          disabled:
                            selectedDealIds.length > 20 ||
                            (bulkDownloadState.status !== 'idle' &&
                              bulkDownloadState.status !== 'completed' &&
                              bulkDownloadState.status !== 'failed'),
                          disabledTooltipText:
                            selectedDealIds.length > 20
                              ? 'Maximum 20 deals can be selected for spec sheet download'
                              : 'A download is already in progress',
                        },
                      ]
                    : []),
                  {
                    label: 'Assign Account Owner',
                    icon: <BsPerson size={20} />,
                    callback: () => setShowSelectAccountOwner(true),
                  },
                  {
                    label: 'Change Sales Stage',
                    icon: <BsSignpost size={20} />,
                    callback: () => setShowSelectSalesStage(true),
                  },
                  {
                    label: 'Manage Override',
                    icon: <BsPencil size={20} />,
                    callback: () => setShowOverrideModal(true),
                  },
                  {
                    label: 'Delete Deals',
                    icon: <BsXCircle size={20} />,
                    callback: () => bulkDeleteDeals(),
                  },
                ]}
                selectedRowsCount={totalSelectedRows}
                entityType="companies"
              />
              <FilterChips
                identifier={IDENTIFIER}
                store={dealsFilterStore}
                showActive
                clearAllButton
              />
            </div>
          </S.SearchContainer>
        }
        key={props.tableKey}
        tableKey={props.tableKey}
        loading={isFetching}
        data={(data?.results ?? []) as any}
        columns={columns}
        sortableFields={sortableFields}
        virtualizeRows={false}
        enableRowSelection
        isPaginationEnabled={props.enablePagination}
        defaultSort={[{ id: 'annual_revenue', desc: true }]}
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        stickyLastColumn
        expandColAccessor={'expanded-row'}
        selectAllText="Select Deals"
        extraCellColSpan={6}
        footerControls={
          <OverlayTrigger placement="top" overlay={tooltip}>
            <ExportButton
              variant="secondary"
              onClick={() => setShowExportModal(true)}
            >
              <BsDownload size={18} />
            </ExportButton>
          </OverlayTrigger>
        }
      />

      <EditDealModalV2
        onClose={() => setDealReportToEdit(undefined)}
        onDealEdited={() => {
          // Invalidate all queries for this table to ensure cache is properly cleared
          queryClient.invalidateQueries({
            queryKey: [props.tableKey],
            exact: false,
          })
        }}
        show={!!dealReportToEdit}
        dealReport={dealReportToEdit}
      />

      <DealExportModal
        tableTypeSlug="deals-table-reports"
        count={data?.count ?? 0}
        show={showExportModal}
        _handleClose={() => setShowExportModal(false)}
        filters={filterAndSortParams}
      />

      <TagAssignModal
        show={showAssignTags}
        handleClose={() => setShowAssignTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        tableObjAccessor="cc_taglist"
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) => {
          return api.commonManageTags({
            values: {
              ...values,
              filter_params: filterAndSortParams,
            },
            tableType: 'deals-table-reports',
          })
        }}
        idAccessor="cc_id"
      />

      <DistributorAssignModal
        show={showAssignDistributors}
        handleClose={() => setShowAssignDistributors(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="cc_id"
        tableObjAccessor="cc_distributors"
        filterAndSortParams={filterAndSortParams}
        apiDistributorsAction={(values) =>
          api.commonManageDistributors({
            tableType: 'deals-table-reports',
            values: {
              ...values,
              filter_params: filterAndSortParams,
            },
          })
        }
      />

      <GPOAssignModal
        show={showAssignGPOs}
        handleClose={() => setShowAssignGPOs(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="cc_id"
        tableObjAccessor="cc_gpos"
        filterAndSortParams={filterAndSortParams}
        apiGPOsAction={(values) =>
          api.commonManageGPOs({
            tableType: 'deals-table-reports',
            values: {
              ...values,
              filter_params: filterAndSortParams,
            },
          })
        }
      />

      <AccountOwnerSelectModal
        show={showSelectAccountOwner}
        handleClose={() => setShowSelectAccountOwner(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="id"
        filterAndSortParams={filterAndSortParams}
        useReportsEndpoint={true}
      />

      <SalesStageSelectModal
        show={showSelectSalesStage}
        handleClose={() => setShowSelectSalesStage(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="id"
        filterAndSortParams={filterAndSortParams}
        useReportsEndpoint={true}
      />

      <DealsOverrideModal
        show={showOverrideModal}
        handleClose={() => setShowOverrideModal(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="id"
        filterAndSortParams={filterAndSortParams}
        useReportsEndpoint={true}
      />

      {featureFlag.enableReminders && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultCompanyId={selectedCompanyId}
          defaultDealIds={selectedDealIds}
          onCreated={() => {
            setShowReminderModal(false)
          }}
        />
      )}
    </DataTableContainer>
  )
}
